/**
 * Bathroom size buckets (Phase 6). Same structural pattern as kitchen's
 * size ranges (see kitchen-size-ranges.ts's doc comment for the full
 * rationale) - a bathroom-specific bucket table, shared by the
 * `bathroom_size_range` question definition (questions/bathroom.ts) and
 * the estimator app's bucketed size step, so the engine itself never
 * sees a bucket id, only a resolved `roomSize` (a plain `Dimensioned`
 * sqft value). Swapping this bucketed question for a direct numeric one
 * later requires no engine or pricing change at all.
 *
 * Bucket boundaries reflect typical Canadian residential bathroom sizes:
 * a powder room/small ensuite, a standard full bath, a larger bath or
 * primary ensuite, and a spa-like/luxury primary bath.
 */
export interface BathroomSizeRange {
  id: string;
  label: string;
  /** Representative square footage used to drive the calculation when this bucket is picked. */
  representativeSqft: number;
  min: number;
  /** null = no upper bound ("90+ sq. ft."). */
  max: number | null;
}

export const BATHROOM_SIZE_RANGES: BathroomSizeRange[] = [
  { id: "under_35", label: "Under 35 sq. ft.", representativeSqft: 25, min: 0, max: 35 },
  { id: "35_55", label: "35–55 sq. ft.", representativeSqft: 45, min: 35, max: 55 },
  { id: "55_90", label: "55–90 sq. ft.", representativeSqft: 70, min: 55, max: 90 },
  { id: "90_plus", label: "90+ sq. ft.", representativeSqft: 110, min: 90, max: null }
];

export function findBathroomSizeRange(id: string | undefined): BathroomSizeRange | undefined {
  return BATHROOM_SIZE_RANGES.find((r) => r.id === id);
}

/** Finds the bucket a given exact square footage falls into, for keeping the bucket answer and an exact value in sync. */
export function bathroomSizeRangeForSqft(sqft: number): BathroomSizeRange | undefined {
  return BATHROOM_SIZE_RANGES.find((r) => sqft >= r.min && (r.max === null || sqft < r.max)) ?? BATHROOM_SIZE_RANGES[BATHROOM_SIZE_RANGES.length - 1];
}
